import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, Link, useSearchParams, useNavigate } from 'react-router-dom';
import { reportService } from '../../services/reportService';
import {
  ReportFilters,
  SegmentedDashboardData,
  ItemMode,
  YearEndProjection,
} from '../../types/Report';
import FilterBar from '../../components/reports/FilterBar';
import ReportHeader from '../../components/reports/ReportHeader';
import DetailedItemsTable from '../../components/reports/DetailedItemsTable';
import ExportButton from '../../components/reports/ExportButton';
import { appendReportFiltersToSearchParams } from '../../utils/reportFilters';
import { has_active_comparison, parse_compare_mode } from '../../utils/compareMode';
import { format_yoy_change_percent } from '../../utils/yoyDisplay';

const ExecutiveDashboardSegmentedPage: React.FC = () => {
  const { type, id } = useParams<{ type: string; id: string }>();
  const [searchParams, _setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [segmentedData, setSegmentedData] = useState<SegmentedDashboardData | null>(null);
  const [mode] = useState<ItemMode>('decliners');
  const [exporting, setExporting] = useState(false);

  // Initialize default filters (first day of current year to today, YOY comparison)
  const getDefaultFilters = (): ReportFilters => {
    const now = new Date();
    const endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startDate = new Date(now.getFullYear(), 0, 1); // January 1st of current year
    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
      compareMode: 'yoy',
    };
  };

  // Read filters from query params on mount
  const getFiltersFromParams = (): ReportFilters => {
    const defaultFilters = getDefaultFilters();
    const filters: ReportFilters = {
      startDate: searchParams.get('startDate') || defaultFilters.startDate,
      endDate: searchParams.get('endDate') || defaultFilters.endDate,
      compareMode: parse_compare_mode(
        searchParams.get('compareMode') || defaultFilters.compareMode
      ),
    };
    const customerIdsParam = searchParams.get('customerIds');
    if (customerIdsParam) {
      filters.customerIds = customerIdsParam
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
    }
    const productIdsParam = searchParams.get('productIds');
    if (productIdsParam) {
      filters.productIds = productIdsParam
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0)
        .map((s) => parseInt(s, 10))
        .filter((n) => !isNaN(n));
    }
    const categoryIdsParam = searchParams.get('categoryIds');
    if (categoryIdsParam) {
      filters.categoryIds = categoryIdsParam
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
    }
    const brandIdsParam = searchParams.get('brandIds');
    if (brandIdsParam) {
      filters.brandIds = brandIdsParam
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
    }
    const territoryIdsParam = searchParams.get('territoryIds');
    if (territoryIdsParam) {
      filters.territoryIds = territoryIdsParam
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
    }
    const salesPersonIdsParam = searchParams.get('salesPersonIds');
    if (salesPersonIdsParam) {
      filters.salesPersonIds = salesPersonIdsParam
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
    }
    const primaryDimensionParam = searchParams.get('primary_dimension');
    if (primaryDimensionParam) filters.primary_dimension = primaryDimensionParam.trim();
    const secondaryDimensionParam = searchParams.get('secondary_dimension');
    if (secondaryDimensionParam) filters.secondary_dimension = secondaryDimensionParam.trim();
    return filters;
  };

  const [filters, setFilters] = useState<ReportFilters>(getFiltersFromParams());

  const mainDashboardHref = useMemo(
    () => `/reports/executive-dashboard?${appendReportFiltersToSearchParams(filters).toString()}`,
    [filters]
  );

  // Detect sidebar width for loader positioning
  const [sidebarOffset, setSidebarOffset] = useState(250);
  useEffect(() => {
    const updateSidebarOffset = () => {
      const mainContent = document.querySelector('.main-content');
      if (mainContent) {
        const computedStyle = window.getComputedStyle(mainContent);
        const marginLeft = parseInt(computedStyle.marginLeft, 10) || 250;
        setSidebarOffset(marginLeft);
      }
    };
    updateSidebarOffset();
    window.addEventListener('resize', updateSidebarOffset);
    const observer = new MutationObserver(updateSidebarOffset);
    const mainContent = document.querySelector('.main-content');
    if (mainContent) {
      observer.observe(mainContent, { attributes: true, attributeFilter: ['class'] });
    }
    return () => {
      window.removeEventListener('resize', updateSidebarOffset);
      observer.disconnect();
    };
  }, []);

  // Generate correlation ID when type/id changes (shared across all requests for this page load)
  const correlationId = useMemo(() => {
    if (type && id) {
      const correlationIdValue = `segmented-${type}-${id}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      console.log(`[Frontend] Generated correlation ID: ${correlationIdValue}`);
      return correlationIdValue;
    }
    const correlationIdValue = `segmented-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    console.log(`[Frontend] Generated correlation ID (no type/id): ${correlationIdValue}`);
    return correlationIdValue;
  }, [type, id]);

  const isLoadingRef = useRef(false);
  const lastLoadParamsRef = useRef<string>('');

  const loadSegmentedData = useCallback(async () => {
    if (!type || !id) return;

    // Create a unique key for this load request to prevent duplicates
    const loadKey = `${type}-${id}-${JSON.stringify(filters)}-${mode}`;

    // Prevent duplicate calls with the same parameters
    if (isLoadingRef.current && lastLoadParamsRef.current === loadKey) {
      return;
    }

    isLoadingRef.current = true;
    lastLoadParamsRef.current = loadKey;
    setLoading(true);
    setError(null);
    try {
      const data = await reportService.getSegmentedData(
        type as 'customer' | 'product' | 'salesagent' | 'territory' | 'category' | 'brand',
        id,
        filters,
        mode,
        undefined, // limit
        correlationId
      );
      setSegmentedData(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load segmented data');
      console.error('Error loading segmented data:', err);
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, [type, id, filters, mode]);

  // Read query params on mount and when they change
  useEffect(() => {
    setFilters(getFiltersFromParams());
  }, [searchParams]);

  useEffect(() => {
    loadSegmentedData();
  }, [loadSegmentedData]);

  const handleFiltersChange = useCallback((newFilters: ReportFilters) => {
    setFilters(newFilters);
  }, []);

  const handleRun = useCallback(() => {
    if (!type || !id) return;

    const params = appendReportFiltersToSearchParams(filters);
    navigate(
      `/reports/executive-dashboard/${type}/${encodeURIComponent(id)}?${params.toString()}`,
      {
        replace: true,
      }
    );

    loadSegmentedData();
  }, [type, id, filters, navigate, loadSegmentedData]);

  const handleReset = () => {
    const defaultFilters = getDefaultFilters();
    setFilters(defaultFilters);
    loadSegmentedData();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value);
  };

  const YearEndProjectionWidget: React.FC<{ projection: YearEndProjection | null }> = ({
    projection,
  }) => {
    const [showDetails, setShowDetails] = useState(false);

    if (!projection) {
      return (
        <div
          style={{
            backgroundColor: 'var(--report-surface)',
            border: '1px solid var(--report-border)',
            borderRadius: '8px',
            padding: '1.5rem',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          }}
        >
          <div
            style={{
              fontSize: '1.1rem',
              fontWeight: '600',
              marginBottom: '1rem',
              color: 'var(--report-text)',
            }}
          >
            Year-end Sales Projection
          </div>
          <div style={{ color: 'var(--report-text-muted)', fontStyle: 'italic' }}>
            Loading projection data...
          </div>
        </div>
      );
    }

    const isPositive = projection.growthPercent !== null && projection.growthPercent >= 0;
    const growthColor = isPositive ? '#28a745' : '#dc3545';
    const growthIndicator = isPositive ? '▲' : '▼';

    return (
      <div
        style={{
          backgroundColor: 'var(--report-surface)',
          border: '1px solid var(--report-border)',
          borderRadius: '8px',
          padding: '1.5rem',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem',
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: '1.1rem',
              fontWeight: '600',
              color: 'var(--report-text)',
            }}
          >
            Year-end Sales Projection
          </h3>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <button
              onClick={() => setShowDetails(!showDetails)}
              style={{
                padding: '0.25rem 0.5rem',
                backgroundColor: showDetails ? 'var(--report-text-muted)' : 'var(--report-accent)',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.85rem',
              }}
            >
              {showDetails ? 'Hide' : 'Details'}
            </button>
          </div>
        </div>

        {!projection.hasData ? (
          <div>
            <div style={{ color: '#dc3545', fontWeight: '500', marginBottom: '0.5rem' }}>N/A</div>
            {projection.warning && (
              <div
                style={{
                  fontSize: '0.85rem',
                  color: 'var(--report-text-muted)',
                  fontStyle: 'italic',
                }}
              >
                {projection.warning}
              </div>
            )}
          </div>
        ) : (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr 1.5fr',
                gap: '1rem',
                padding: '1rem',
                backgroundColor: 'var(--report-surface-subtle)',
                borderRadius: '4px',
                border: '1px solid var(--report-border)',
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: '0.75rem',
                    color: 'var(--report-text-muted)',
                    marginBottom: '0.25rem',
                  }}
                >
                  Current YTD
                </div>
                <div style={{ fontSize: '1.1rem', fontWeight: '600' }}>
                  {formatCurrency(projection.currentYtd)}
                </div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: '0.75rem',
                    color: 'var(--report-text-muted)',
                    marginBottom: '0.25rem',
                  }}
                >
                  Last YTD
                </div>
                <div style={{ fontSize: '1.1rem', fontWeight: '600' }}>
                  {formatCurrency(projection.lastYtd)}
                </div>
              </div>
              {projection.growthPercent !== null && (
                <div>
                  <div
                    style={{
                      fontSize: '0.75rem',
                      color: 'var(--report-text-muted)',
                      marginBottom: '0.25rem',
                    }}
                  >
                    Growth %
                  </div>
                  <div
                    style={{
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      color: growthColor,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                    }}
                  >
                    <span>{growthIndicator}</span>
                    <span>{formatPercentage(projection.growthPercent)}</span>
                  </div>
                </div>
              )}
              {projection.projectedFullYear !== null && (
                <div>
                  <div
                    style={{
                      fontSize: '0.75rem',
                      color: 'var(--report-text-muted)',
                      marginBottom: '0.25rem',
                    }}
                  >
                    Projected Full-Year Sales
                  </div>
                  <div
                    style={{ fontSize: '1.3rem', fontWeight: '700', color: 'var(--report-accent)' }}
                  >
                    {formatCurrency(projection.projectedFullYear)}
                  </div>
                </div>
              )}
            </div>

            {showDetails && (
              <div
                style={{
                  marginTop: '1rem',
                  padding: '1rem',
                  backgroundColor: 'var(--report-surface-subtle)',
                  borderRadius: '4px',
                  fontSize: '0.85rem',
                }}
              >
                <div style={{ fontWeight: '600', marginBottom: '0.75rem', fontSize: '0.9rem' }}>
                  Calculation Formula:
                </div>
                <div
                  style={{
                    backgroundColor: 'var(--report-surface)',
                    padding: '0.75rem',
                    borderRadius: '4px',
                    marginBottom: '1rem',
                    fontFamily: 'monospace',
                    fontSize: '0.8rem',
                    border: '1px solid var(--report-border)',
                  }}
                >
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Growth %</strong> = ((Current YTD - Last YTD) / Last YTD) × 100
                  </div>
                  <div style={{ marginBottom: '0.5rem' }}>
                    = (({formatCurrency(projection.currentYtd)} -{' '}
                    {formatCurrency(projection.lastYtd)}) / {formatCurrency(projection.lastYtd)}) ×
                    100
                  </div>
                  <div style={{ color: growthColor, fontWeight: '600' }}>
                    = {formatPercentage(projection.growthPercent || 0)}
                  </div>
                  <div
                    style={{
                      marginTop: '0.75rem',
                      paddingTop: '0.75rem',
                      borderTop: '1px solid var(--report-border)',
                    }}
                  >
                    <div
                      style={{
                        marginBottom: '0.5rem',
                        fontSize: '0.75rem',
                        color: 'var(--report-text-muted)',
                      }}
                    >
                      <strong>Step 1:</strong> Calculate Seasonality Factor (for remaining period)
                    </div>
                    <div style={{ marginBottom: '0.5rem' }}>
                      <strong>Seasonality Factor</strong> = Last Year Remaining / Last YTD
                    </div>
                    <div style={{ marginBottom: '0.5rem' }}>
                      = {formatCurrency(projection.lastYearRemaining || 0)} /{' '}
                      {formatCurrency(projection.lastYtd)}
                    </div>
                    <div style={{ color: '#28a745', fontWeight: '600', marginBottom: '0.75rem' }}>
                      = {projection.seasonalityFactor?.toFixed(4) || 'N/A'}
                    </div>
                    <div
                      style={{
                        marginBottom: '0.5rem',
                        fontSize: '0.75rem',
                        color: 'var(--report-text-muted)',
                      }}
                    >
                      <strong>Step 2:</strong> Project Remaining Period
                    </div>
                    <div style={{ marginBottom: '0.5rem' }}>
                      <strong>Projected Remaining</strong> = Current YTD × Seasonality Factor
                    </div>
                    <div style={{ marginBottom: '0.5rem' }}>
                      = {formatCurrency(projection.currentYtd)} ×{' '}
                      {projection.seasonalityFactor?.toFixed(4) || 'N/A'}
                    </div>
                    <div style={{ color: '#17a2b8', fontWeight: '600', marginBottom: '0.75rem' }}>
                      = {formatCurrency(projection.projectedRemaining || 0)}
                    </div>
                    <div
                      style={{
                        marginBottom: '0.5rem',
                        fontSize: '0.75rem',
                        color: 'var(--report-text-muted)',
                      }}
                    >
                      <strong>Step 3:</strong> Calculate Full-Year Projection
                    </div>
                    <div style={{ marginBottom: '0.5rem' }}>
                      <strong>Projected Full-Year Sales</strong> = Current YTD + Projected Remaining
                    </div>
                    <div style={{ marginBottom: '0.5rem' }}>
                      = {formatCurrency(projection.currentYtd)} +{' '}
                      {formatCurrency(projection.projectedRemaining || 0)}
                    </div>
                    <div style={{ color: 'var(--report-accent)', fontWeight: '600' }}>
                      = {formatCurrency(projection.projectedFullYear || 0)}
                    </div>
                  </div>
                </div>

                <div style={{ fontWeight: '600', marginBottom: '0.5rem' }}>Source Numbers:</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                  <div>
                    <strong>Current YTD:</strong> {formatCurrency(projection.currentYtd)}
                  </div>
                  <div>
                    <strong>Last YTD:</strong> {formatCurrency(projection.lastYtd)}
                  </div>
                  <div>
                    <strong>Last Year Remaining:</strong>{' '}
                    {formatCurrency(projection.lastYearRemaining || 0)}
                  </div>
                  <div>
                    <strong>Last Year Full-Year:</strong> {formatCurrency(projection.lastYearSales)}
                  </div>
                  <div>
                    <strong>Seasonality Factor:</strong>{' '}
                    {projection.seasonalityFactor?.toFixed(4) || 'N/A'}
                  </div>
                  <div>
                    <strong>Projected Remaining:</strong>{' '}
                    {formatCurrency(projection.projectedRemaining || 0)}
                  </div>
                </div>
                <div style={{ marginTop: '0.75rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                  Time Period:
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                  <div>
                    <strong>Days Elapsed:</strong> {projection.daysElapsed} days
                  </div>
                  <div>
                    <strong>Days Remaining:</strong> {projection.daysRemaining} days
                  </div>
                </div>
                <div style={{ marginTop: '0.75rem', fontWeight: '600' }}>Date Ranges:</div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '0.5rem',
                    marginTop: '0.25rem',
                  }}
                >
                  <div>
                    <strong>Current YTD:</strong> {projection.dateRanges.currentYtdStart} to{' '}
                    {projection.dateRanges.currentYtdEnd}
                  </div>
                  <div>
                    <strong>Last YTD:</strong> {projection.dateRanges.lastYtdStart} to{' '}
                    {projection.dateRanges.lastYtdEnd}
                  </div>
                  <div>
                    <strong>Last Year Remaining:</strong>{' '}
                    {projection.dateRanges.lastYearRemainingStart} to{' '}
                    {projection.dateRanges.lastYearRemainingEnd}
                  </div>
                  <div>
                    <strong>Last Year Full-Year:</strong> {projection.dateRanges.lastYearStart} to{' '}
                    {projection.dateRanges.lastYearEnd}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const handleExportKPI = async (metric: 'revenue' | 'orders', title: string) => {
    try {
      setError(null);
      const normalizedType =
        type === 'salesagent'
          ? 'salesagent'
          : type === 'salesAgent'
            ? 'salesagent'
            : (type as 'customer' | 'product' | 'territory' | 'category' | 'brand' | undefined);
      const blob = await reportService.exportExecutiveDashboardKPI(
        metric,
        filters,
        normalizedType,
        id
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const dateRange =
        filters.startDate && filters.endDate
          ? `${filters.startDate}_to_${filters.endDate}`
          : new Date().toISOString().split('T')[0];
      const safeId = String(id || 'item').replace(/[^a-zA-Z0-9]/g, '_');
      a.download = `executive-dashboard-${type}-${safeId}-${title.toLowerCase().replace(/\s+/g, '-')}-${dateRange}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      setError(err.message || 'Failed to export Excel file');
      console.error('Error exporting Excel:', err);
    }
  };

  const handleExportSegmentedData = async () => {
    if (!type || !id || exporting) return;
    setExporting(true);
    try {
      const blob = await reportService.exportSegmentedData(
        type as 'customer' | 'product' | 'salesagent' | 'territory' | 'category' | 'brand',
        id,
        filters
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const dateRange =
        filters.startDate && filters.endDate
          ? `${filters.startDate}_to_${filters.endDate}`
          : new Date().toISOString().split('T')[0];
      const safeId = String(id).replace(/[^a-zA-Z0-9]/g, '_');
      a.download = `executive-dashboard-${type}-${safeId}-${dateRange}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      setError(err.message || 'Failed to export Excel file');
      console.error('Error exporting segmented data:', err);
    } finally {
      setExporting(false);
    }
  };

  const KPITile: React.FC<{
    title: string;
    data: { current: number; prior: number; change: number; changePercent: number };
    formatter: (value: number) => string;
    onExport?: () => void;
  }> = ({ title, data, formatter, onExport }) => {
    const isPositive = data.change >= 0;
    const changeColor = isPositive ? '#28a745' : '#dc3545';

    return (
      <div
        style={{
          backgroundColor: 'var(--report-surface)',
          border: '1px solid var(--report-border)',
          borderRadius: '8px',
          padding: '1.5rem',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '0.5rem',
          }}
        >
          <div style={{ fontSize: '0.9rem', color: 'var(--report-text-muted)' }}>{title}</div>
          {onExport && (
            <ExportButton
              onClick={onExport}
              size="small"
              variant="primary"
              title={`Export ${title} details`}
            />
          )}
        </div>
        <div
          style={{
            fontSize: '2rem',
            fontWeight: '600',
            marginBottom: '0.5rem',
            color: 'var(--report-text)',
          }}
        >
          {formatter(data.current)}
        </div>
        {has_active_comparison(filters.compareMode) && (
          <div style={{ fontSize: '0.85rem', color: 'var(--report-text-muted)' }}>
            <div>
              Prior: {formatter(data.prior)} (
              {format_yoy_change_percent(data.prior, data.changePercent, formatPercentage)})
            </div>
            <div style={{ color: changeColor, fontWeight: '500', marginTop: '0.25rem' }}>
              {isPositive ? '↑' : '↓'} {formatter(Math.abs(data.change))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Get secondary segmentation types based on primary type
  const getSecondarySegments = (
    primaryType: string
  ): Array<{ key: string; title: string; drillDownType: string }> => {
    switch (primaryType) {
      case 'customer':
        return [
          { key: 'products', title: 'Products', drillDownType: 'product' },
          { key: 'brands', title: 'Brands', drillDownType: 'brand' },
          { key: 'salesAgents', title: 'Sales Agents', drillDownType: 'salesagent' },
          { key: 'territories', title: 'Territories', drillDownType: 'territory' },
          { key: 'categories', title: 'Categories', drillDownType: 'category' },
        ];
      case 'product':
        return [
          { key: 'customers', title: 'Customers', drillDownType: 'customer' },
          { key: 'salesAgents', title: 'Sales Agents', drillDownType: 'salesagent' },
          { key: 'brands', title: 'Brands', drillDownType: 'brand' },
          { key: 'territories', title: 'Territories', drillDownType: 'territory' },
          { key: 'categories', title: 'Categories', drillDownType: 'category' },
        ];
      case 'territory':
        return [
          { key: 'products', title: 'Products', drillDownType: 'product' },
          { key: 'brands', title: 'Brands', drillDownType: 'brand' },
          { key: 'salesAgents', title: 'Sales Agents', drillDownType: 'salesagent' },
          { key: 'customers', title: 'Customers', drillDownType: 'customer' },
          { key: 'categories', title: 'Categories', drillDownType: 'category' },
        ];
      case 'salesagent':
        return [
          { key: 'customers', title: 'Customers', drillDownType: 'customer' },
          { key: 'products', title: 'Products', drillDownType: 'product' },
          { key: 'brands', title: 'Brands', drillDownType: 'brand' },
          { key: 'territories', title: 'Territories', drillDownType: 'territory' },
          { key: 'categories', title: 'Categories', drillDownType: 'category' },
        ];
      case 'brand':
        return [
          { key: 'customers', title: 'Customers', drillDownType: 'customer' },
          { key: 'salesAgents', title: 'Sales Agents', drillDownType: 'salesagent' },
          { key: 'products', title: 'Products', drillDownType: 'product' },
          { key: 'territories', title: 'Territories', drillDownType: 'territory' },
          { key: 'categories', title: 'Categories', drillDownType: 'category' },
        ];
      case 'category':
        return [
          { key: 'products', title: 'Products', drillDownType: 'product' },
          { key: 'brands', title: 'Brands', drillDownType: 'brand' },
          { key: 'customers', title: 'Customers', drillDownType: 'customer' },
          { key: 'salesAgents', title: 'Sales Agents', drillDownType: 'salesagent' },
          { key: 'territories', title: 'Territories', drillDownType: 'territory' },
        ];
      default:
        return [];
    }
  };

  if (loading && !segmentedData) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--report-text)' }}>
        <div>Loading segmented data...</div>
      </div>
    );
  }

  if (error && !segmentedData) {
    return (
      <div style={{ padding: '2rem', color: 'var(--report-text)' }}>
        <div style={{ color: '#dc3545', marginBottom: '1rem' }}>Error: {error}</div>
        <button
          onClick={loadSegmentedData}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: 'var(--report-accent)',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!type || !id || !segmentedData) {
    return (
      <div style={{ padding: '2rem', color: 'var(--report-text)' }}>
        <div style={{ color: '#dc3545' }}>Invalid URL parameters</div>
        <Link to={mainDashboardHref} style={{ color: 'var(--report-accent)' }}>
          Back to Executive Dashboard
        </Link>
      </div>
    );
  }

  const secondarySegments = getSecondarySegments(type);
  const typeDisplayName =
    type === 'salesagent'
      ? 'Sales Agent'
      : type.charAt(0).toUpperCase() +
        type
          .slice(1)
          .replace(/([A-Z])/g, ' $1')
          .trim();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <ReportHeader
        filters={filters}
        dataFreshness={segmentedData?.dataFreshness}
        title={`Executive Dashboard - ${typeDisplayName}: ${segmentedData.itemName || id}`}
      />
      <FilterBar
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onRun={handleRun}
        onReset={handleReset}
        loading={loading}
        lockedSegment={{
          urlType: type,
          id,
          name: segmentedData?.itemName,
        }}
      />
      <div
        style={{
          flex: 1,
          overflow: 'auto',
          padding: '1.5rem',
          backgroundColor: 'var(--report-surface-subtle)',
          position: 'relative',
        }}
      >
        {loading && segmentedData && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: `${sidebarOffset}px`,
              right: 0,
              bottom: 0,
              backgroundColor: 'var(--report-overlay)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              transition: 'left 0.3s ease',
            }}
          >
            <div
              style={{
                border: '4px solid var(--report-spinner-track)',
                borderTop: '4px solid var(--report-accent)',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                animation: 'spin 1s linear infinite',
                marginBottom: '1rem',
              }}
            />
            <div
              style={{ fontSize: '1.1rem', fontWeight: '500', color: 'var(--report-text-muted)' }}
            >
              Loading segmented data...
            </div>
          </div>
        )}
        {error && segmentedData && (
          <div
            style={{
              marginBottom: '1rem',
              padding: '0.75rem 1rem',
              backgroundColor: 'var(--report-error-bg)',
              border: '1px solid var(--report-error-border)',
              color: 'var(--report-error-text)',
              borderRadius: '4px',
            }}
          >
            {error}
          </div>
        )}

        {/* Breadcrumb */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '1.5rem',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.9rem',
            }}
          >
            <Link
              to="/reports/executive-dashboard"
              style={{ color: 'var(--report-accent)', textDecoration: 'none' }}
            >
              Executive Dashboard
            </Link>
            <span style={{ color: 'var(--report-text-muted)' }}>›</span>
            <span style={{ color: 'var(--report-text-muted)' }}>
              {typeDisplayName}: {segmentedData.itemName || id}
            </span>
          </div>
          <ExportButton
            onClick={handleExportSegmentedData}
            loading={loading || exporting}
            disabled={loading || exporting}
            size="large"
            variant="primary"
            title="Export line items with widget Summary"
          />
        </div>

        {/* KPI Tiles */}
        {segmentedData.kpis && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '1rem',
              marginBottom: '2rem',
            }}
          >
            <KPITile
              title="Revenue"
              data={segmentedData.kpis.revenue}
              formatter={formatCurrency}
              onExport={() => handleExportKPI('revenue', 'Revenue')}
            />
            <KPITile title="Cases" data={segmentedData.kpis.cases} formatter={formatNumber} />
            <KPITile
              title="Active Customers"
              data={segmentedData.kpis.customers}
              formatter={formatNumber}
            />
            <KPITile
              title="Total Orders"
              data={segmentedData.kpis.orders}
              formatter={formatNumber}
              onExport={() => handleExportKPI('orders', 'Total Orders')}
            />
          </div>
        )}

        {/* Year-end Sales Projection is a year-over-year estimate */}
        {segmentedData.yearEndProjection && filters.compareMode === 'yoy' && (
          <div style={{ marginBottom: '2rem' }}>
            <YearEndProjectionWidget projection={segmentedData.yearEndProjection} />
          </div>
        )}

        {/* Detailed Tables */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
          {secondarySegments.map((segment) => {
            return (
              <div
                key={segment.key}
                style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}
              >
                <DetailedItemsTable
                  type={
                    type as
                      | 'customer'
                      | 'product'
                      | 'salesagent'
                      | 'territory'
                      | 'category'
                      | 'brand'
                  }
                  id={id}
                  segmentType={
                    segment.key as
                      | 'products'
                      | 'customers'
                      | 'salesAgents'
                      | 'brands'
                      | 'territories'
                      | 'categories'
                  }
                  filters={filters}
                  mode={mode}
                  title={`Detailed ${segment.title} Data`}
                  correlationId={correlationId}
                />
              </div>
            );
          })}
        </div>

        {secondarySegments.length === 0 && (
          <div
            style={{
              textAlign: 'center',
              padding: '2rem',
              color: 'var(--report-text-muted)',
              fontStyle: 'italic',
            }}
          >
            No secondary segmentation data available for this item.
          </div>
        )}
      </div>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default ExecutiveDashboardSegmentedPage;
